/**
 * zalo-label-import.ts — Lời giải thích (tiếng Việt) cho công cụ "Nhập nhãn từ Zalo" trong hộp Thêm nhãn: câu tóm tắt xem trước, kết quả nhập,
 * điều kiện cho phép bấm "Nhập". Hàm thuần — giao diện: pages/conversations/zalo-label-import-dialog.tsx; backend: modules/zalo/zalo-label-import.ts.
 */
import { formatNumber } from './utils'
import { i18n, currentLocale } from '@/lib/i18n'

export interface ZaloLabelTotals {
  labels: number
  newTags: number
  mergedTags: number
  conversations: number
  matchedConversations: number
  unmatchedConversations: number
  contactsToTag: number
  groupsToTag: number
  assignments: number
}

/** "12 nhãn Zalo (4 mới, 8 trùng tên nhãn eCDP — gộp)". */
export function labelsHeadline(t: ZaloLabelTotals): string {
  if (t.labels === 0) return i18n.t('chanCatalog:labelImport.noLabels')
  const parts: string[] = []
  if (t.newTags > 0) parts.push(i18n.t('chanCatalog:labelImport.newTags', { n: formatNumber(t.newTags) }))
  if (t.mergedTags > 0) parts.push(i18n.t('chanCatalog:labelImport.mergedTags', { n: formatNumber(t.mergedTags) }))
  const n = formatNumber(t.labels)
  return parts.length
    ? i18n.t('chanCatalog:labelImport.labelsCountWithParts', { n, parts: parts.join(', ') })
    : i18n.t('chanCatalog:labelImport.labelsCount', { n })
}

/** "Sẽ gắn nhãn cho 340 khách + 12 nhóm" / "Không còn khách nào cần gắn thêm nhãn". `past` = đã nhập xong. */
export function assignHeadline(t: Pick<ZaloLabelTotals, 'contactsToTag' | 'groupsToTag' | 'assignments'>, past = false): string {
  if (t.assignments === 0) return i18n.t(past ? 'chanCatalog:labelImport.assignNonePast' : 'chanCatalog:labelImport.assignNoneFuture')
  const who = [t.contactsToTag > 0 ? i18n.t('chanCatalog:labelImport.contacts', { n: formatNumber(t.contactsToTag) }) : '', t.groupsToTag > 0 ? i18n.t('chanCatalog:labelImport.groups', { n: formatNumber(t.groupsToTag) }) : ''].filter(Boolean).join(' + ')
  return i18n.t(past ? 'chanCatalog:labelImport.assignPast' : 'chanCatalog:labelImport.assignFuture', { who })
}

/** "15 hội thoại chưa có trong eCDP — bỏ qua" (rỗng khi không có). */
export function unmatchedNote(t: Pick<ZaloLabelTotals, 'unmatchedConversations'>): string {
  return t.unmatchedConversations > 0 ? i18n.t('chanCatalog:labelImport.unmatched', { n: formatNumber(t.unmatchedConversations) }) : ''
}

/** Có gì để nhập không (có nhãn mới cần tạo hoặc có khách cần gắn thêm). */
export function canImport(t: Pick<ZaloLabelTotals, 'newTags' | 'assignments'>): boolean {
  return t.newTags > 0 || t.assignments > 0
}

/** Kết quả sau khi nhập: "Đã tạo 4 nhãn mới. Đã gắn nhãn cho 340 khách + 12 nhóm." */
export function importResultLine(t: ZaloLabelTotals): string {
  const created = t.newTags > 0 ? i18n.t('chanCatalog:labelImport.created', { n: formatNumber(t.newTags) }) + ' ' : ''
  return i18n.t('chanCatalog:labelImport.resultLine', { created, assign: assignHeadline(t, true) })
}

/** Giờ lấy nhãn (HH:mm, giờ máy); không đọc được → rỗng. */
export function fetchedAtLabel(iso: string | undefined): string {
  const d = iso ? new Date(iso) : null
  if (!d || Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' })
}
